<template>
  <div
    class="bg-card border border-border rounded-xl overflow-hidden shadow-sm"
    :aria-label="`Tableau budget ${accountLabel}`"
  >
    <!-- Table header -->
    <div class="flex flex-wrap items-center justify-between gap-4 px-5 py-3 border-b border-border bg-muted/30">
      
      <!-- Segmented Tab Switcher on the left -->
      <div class="flex p-1 bg-muted/50 border border-border/50 rounded-xl">
        <button
          type="button"
          @click="emit('changeAccount', 'personal')"
          class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 border"
          :class="account === 'personal'
            ? 'bg-background border-border/80 text-foreground shadow-sm font-black'
            : 'bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20 font-bold'"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          Compte Personnel
        </button>
        <button
          type="button"
          @click="emit('changeAccount', 'professional')"
          class="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 border"
          :class="account === 'professional'
            ? 'bg-background border-border/80 text-foreground shadow-sm font-black'
            : 'bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/20 font-bold'"
        >
          <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </svg>
          Compte Professionnel
        </button>
      </div>

      <!-- Right actions -->
      <div class="flex items-center gap-3">
        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full border border-border"
          :class="account === 'professional' ? 'text-indigo-400 border-indigo-500/30 bg-indigo-500/10' : 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'"
        >
          {{ year }}
        </span>

        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-border hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          @click="emit('addCategory')"
          :disabled="isReadOnlyMode"
        >
          <span class="text-base leading-none">+</span> Ajouter une catégorie
        </button>
      </div>
    </div>

    <!-- Scrollable table wrapper -->
    <div class="overflow-x-auto custom-scrollbar">
      <table class="w-full border-separate border-spacing-0 text-sm min-w-[1200px]">
        <!-- Month headers -->
        <thead>
          <tr class="border-b border-border">
            <th class="sticky left-0 z-20 bg-card budget-sticky-cell px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground w-52 min-w-52">
              Catégorie
            </th>
            <th
              v-for="(name, i) in monthNames"
              :key="i"
              class="min-w-[90px] px-2 py-2.5 text-right text-[10px] font-black uppercase tracking-widest"
              :class="[
                (i + 1) === currentMonth ? 'text-primary bg-primary/5' : 'text-muted-foreground',
                (i + 1) % 2 === 0 ? 'bg-muted/30' : ''
              ]"
            >
              {{ name }}
            </th>
            <th class="w-28 min-w-[7rem] px-3 py-2.5 text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground">
              Total
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- Opening balance row -->
          <BudgetOpeningRow
            :balance-data="resolvedOpeningBalances"
            :current-month="currentMonth"
            @save="onSaveOpeningBalance"
            @reset="onResetOpeningBalance"
          />

          <!-- INCOME section -->
          <template v-if="incomeCategories.length > 0">
            <!-- Section header -->
            <tr class="budget-section-row border-y border-emerald-500/15">
              <td class="sticky left-0 z-20 px-3 py-1.5 w-52 min-w-52 budget-sticky-section bg-emerald-500/5">
                <span class="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                  ↑ Recettes
                </span>
              </td>
              <td colspan="13" class="bg-emerald-500/5"></td>
            </tr>

            <!-- Income category rows -->
            <tr
              v-for="cat in incomeCategories"
              :key="cat.id"
              class="border-b border-border/30 budget-data-row group/row transition-colors"
            >
              <td class="sticky left-0 z-20 bg-card budget-sticky-cell transition-colors px-3 py-1.5 text-xs font-semibold text-foreground whitespace-nowrap w-52 min-w-52">
                <div class="flex items-center gap-1.5 max-w-full truncate">
                  <span v-if="cat.formula" class="text-[9px] font-black text-muted-foreground/50 shrink-0" title="Catégorie avec formule de calcul">ƒ</span>
                  <span v-if="cat.isNonEditable" class="shrink-0 flex items-center select-none" title="Verrouillé : Cette catégorie est non modifiable manuellement (définie par formule)">
                    <CategoryPropertyIcon name="lock" class="w-3.5 h-3.5" />
                  </span>
                  <span class="truncate" :title="cat.name">{{ cat.name }}</span>
                  <div class="flex items-center gap-1 shrink-0 ml-0.5 opacity-80">
                    <CategoryPropertyIcon v-if="cat.isTaxable" name="tax" class="w-3.5 h-3.5" title="Revenu Imposable (IR)" />
                    <CategoryPropertyIcon v-if="cat.corporateTaxDirection === 'income'" name="is" colorClass="text-emerald-500" class="w-3.5 h-3.5" title="Revenu (IS)" />
                  </div>
                  <button
                    v-if="!isReadOnlyMode"
                    class="opacity-30 hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity ml-auto shrink-0"
                    @click="emit('editCategory', cat)"
                    :title="`Modifier ${cat.name}`"
                    aria-label="Modifier la catégorie"
                  >
                    <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </td>

              <td v-for="month in 12" :key="month" class="min-w-[90px] p-0" :class="month % 2 === 0 ? 'bg-muted/30' : ''">
                <BudgetCell
                  :category-name="cat.name"
                  :month-label="monthNames[month - 1]"
                  type="income"
                  :stored-value="cat.months[month] ?? null"
                  :resolved-value="resolvedValues[cat.id]?.[month] ?? null"
                  :has-formula="!!cat.formula"
                  :is-manual-override="cat.monthOverrides[month] ?? false"
                  :is-current-month="month === currentMonth"
                  :is-non-editable="cat.isNonEditable"
                  @save="(amount) => onSaveLine(cat.id, month, amount)"
                  @reset="onResetLine(cat.id, month)"
                />
              </td>

              <!-- Annual total per category -->
              <td class="w-28 min-w-[7rem] px-3 py-1.5 text-right text-xs font-bold text-emerald-500 dark:text-emerald-400 tabular-nums">
                {{ formatCurrency(catAnnualTotal(cat.id)) }}
              </td>
            </tr>

          </template>

          <!-- EXPENSE section -->
          <template v-if="expenseCategories.length > 0">
            <tr class="budget-section-row border-y border-red-500/15">
              <td class="sticky left-0 z-20 px-3 py-1.5 w-52 min-w-52 budget-sticky-section bg-red-500/5">
                <span class="text-[10px] font-black uppercase tracking-widest text-red-600 dark:text-red-400">
                  ↓ Dépenses
                </span>
              </td>
              <td colspan="13" class="bg-red-500/5"></td>
            </tr>

            <tr
              v-for="cat in expenseCategories"
              :key="cat.id"
              class="border-b border-border/30 budget-data-row group/row transition-colors"
            >
              <td class="sticky left-0 z-20 bg-card budget-sticky-cell transition-colors px-3 py-1.5 text-xs font-semibold text-foreground whitespace-nowrap w-52 min-w-52">
                <div class="flex items-center gap-1.5 max-w-full truncate">
                  <span v-if="cat.formula" class="text-[9px] font-black text-muted-foreground/50 shrink-0" title="Catégorie avec formule de calcul">ƒ</span>
                  <span v-if="cat.isNonEditable" class="shrink-0 flex items-center select-none" title="Verrouillé : Cette catégorie est non modifiable manuellement (définie par formule)">
                    <CategoryPropertyIcon name="lock" class="w-3.5 h-3.5" />
                  </span>
                  <span class="truncate" :title="cat.name">{{ cat.name }}</span>
                  <div class="flex items-center gap-1 shrink-0 ml-0.5 opacity-80">
                    <CategoryPropertyIcon v-if="cat.isTnsRemuneration" name="tns" class="w-3.5 h-3.5" title="Base URSSAF (TNS)" />
                    <CategoryPropertyIcon v-if="cat.isUrssafPayment" name="urssaf" class="w-3.5 h-3.5" title="Acompte URSSAF" />
                    <CategoryPropertyIcon v-if="cat.isTaxPayment" name="tax" class="w-3.5 h-3.5" title="Acompte Impôt (IR)" />
                    <CategoryPropertyIcon v-if="cat.isCorporateTaxPayment" name="is" colorClass="text-emerald-500" class="w-3.5 h-3.5" title="Acompte IS" />
                    <CategoryPropertyIcon v-if="cat.corporateTaxDirection === 'expense'" name="is" colorClass="text-red-500" class="w-3.5 h-3.5" title="Charge (IS)" />
                  </div>
                  <button
                    v-if="!isReadOnlyMode"
                    class="opacity-30 hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity ml-auto shrink-0"
                    @click="emit('editCategory', cat)"
                    :title="`Modifier ${cat.name}`"
                    aria-label="Modifier la catégorie"
                  >
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </td>

              <td v-for="month in 12" :key="month" class="min-w-[90px] p-0" :class="month % 2 === 0 ? 'bg-muted/30' : ''">
                <BudgetCell
                  :category-name="cat.name"
                  :month-label="monthNames[month - 1]"
                  type="expense"
                  :stored-value="cat.months[month] ?? null"
                  :resolved-value="resolvedValues[cat.id]?.[month] ?? null"
                  :has-formula="!!cat.formula"
                  :is-manual-override="cat.monthOverrides[month] ?? false"
                  :is-current-month="month === currentMonth"
                  :is-non-editable="cat.isNonEditable"
                  @save="(amount) => onSaveLine(cat.id, month, amount)"
                  @reset="onResetLine(cat.id, month)"
                />
              </td>

              <td class="w-28 min-w-[7rem] px-3 py-1.5 text-right text-xs font-bold text-red-500 dark:text-red-400 tabular-nums">
                {{ formatCurrency(catAnnualTotal(cat.id)) }}
              </td>
            </tr>
          </template>

          <!-- ── Totals ── -->
          <tr class="border-t-2 border-border budget-total-row">
            <td class="sticky left-0 z-20 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground whitespace-nowrap budget-sticky-total">
              Total recettes
            </td>
            <td
              v-for="month in 12"
              :key="`income-${month}`"
              class="px-2 py-2 text-right text-xs font-bold text-emerald-500 dark:text-emerald-400 tabular-nums"
              :class="month % 2 === 0 ? 'bg-muted/30' : ''"
            >
              {{ formatCurrency(monthlyTotals[month]?.income ?? 0) }}
            </td>
            <td class="px-3 py-2 text-right text-xs font-black text-emerald-500 dark:text-emerald-400 tabular-nums">
              {{ formatCurrency(annualTotals.income) }}
            </td>
          </tr>

          <tr class="border-b border-border budget-total-row">
            <td class="sticky left-0 z-20 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-muted-foreground whitespace-nowrap budget-sticky-total">
              Total dépenses
            </td>
            <td
              v-for="month in 12"
              :key="`expense-${month}`"
              class="px-2 py-2 text-right text-xs font-bold text-red-500 dark:text-red-400 tabular-nums"
              :class="month % 2 === 0 ? 'bg-muted/30' : ''"
            >
              {{ formatCurrency(monthlyTotals[month]?.expense ?? 0) }}
            </td>
            <td class="px-3 py-2 text-right text-xs font-black text-red-500 dark:text-red-400 tabular-nums">
              {{ formatCurrency(annualTotals.expense) }}
            </td>
          </tr>

          <!-- Balance row -->
          <tr class="border-b-2 border-border">
            <td class="sticky left-0 z-20 bg-card px-3 py-2.5 text-[10px] font-black uppercase tracking-wider text-foreground whitespace-nowrap">
              Bilan mensuel
            </td>
            <td
              v-for="month in 12"
              :key="`balance-${month}`"
              class="px-2 py-2.5 text-right text-xs font-black tabular-nums"
              :class="[
                balanceColor(monthlyTotals[month]?.balance ?? 0),
                month % 2 === 0 ? 'bg-muted/30' : ''
              ]"
            >
              {{ formatBalance(monthlyTotals[month]?.balance ?? 0) }}
            </td>
            <td
              class="px-3 py-2.5 text-right text-sm font-black tabular-nums"
              :class="balanceColor(annualTotals.balance)"
            >
              {{ formatBalance(annualTotals.balance) }}
            </td>
          </tr>

          <!-- End of month balance row -->
          <tr class="border-b-2 border-border">
            <td class="sticky left-0 z-20 bg-card px-3 py-2.5 text-[10px] font-black uppercase tracking-wider text-muted-foreground whitespace-nowrap">
              <div class="flex items-center gap-2">
                <span class="text-sky-500/70">🏁</span>
                Solde fin de mois
              </div>
            </td>
            <td
              v-for="month in 12"
              :key="`end-balance-${month}`"
              class="px-2 py-2.5 text-right text-xs font-black tabular-nums"
              :class="[
                openingBalanceColor(getEndOfMonthBalance(month)),
                month % 2 === 0 ? 'bg-muted/30' : ''
              ]"
            >
              {{ formatNullableCurrency(getEndOfMonthBalance(month)) }}
            </td>
            <td
              class="px-3 py-2.5 text-right text-sm font-black tabular-nums"
              :class="openingBalanceColor(getEndOfMonthBalance(12))"
            >
              {{ formatNullableCurrency(getEndOfMonthBalance(12)) }}
            </td>
          </tr>

          <!-- ── Parameters ── -->
          <template v-if="parameterCategories.length > 0">
            <tr class="budget-section-row border-y border-indigo-500/15">
              <td class="sticky left-0 z-20 px-3 py-1.5 w-52 min-w-52 budget-sticky-section bg-indigo-500/5">
                <span class="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
                  ⚙️ Paramètres
                </span>
              </td>
              <td colspan="13" class="bg-indigo-500/5"></td>
            </tr>

            <tr
              v-for="cat in parameterCategories"
              :key="cat.id"
              class="border-b border-border/30 budget-data-row group/row transition-colors"
            >
              <td class="sticky left-0 z-20 bg-card budget-sticky-cell transition-colors px-3 py-1.5 text-xs font-semibold text-foreground whitespace-nowrap w-52 min-w-52">
                <div class="flex items-center gap-1.5 max-w-full truncate">
                  <span v-if="cat.formula" class="text-[9px] font-black text-muted-foreground/50 shrink-0" title="Catégorie avec formule de calcul">ƒ</span>
                  <span v-if="cat.isNonEditable" class="shrink-0 flex items-center select-none" title="Verrouillé : Cette catégorie est non modifiable manuellement (définie par formule)">
                    <CategoryPropertyIcon name="lock" class="w-3.5 h-3.5" />
                  </span>
                  <span class="truncate" :title="cat.name">{{ cat.name }}</span>
                  <div class="flex items-center gap-1 shrink-0 ml-0.5 opacity-80">
                    <CategoryPropertyIcon v-if="cat.isDebt" name="debt" class="w-3.5 h-3.5" title="Est une Dette" />
                    <CategoryPropertyIcon v-if="cat.corporateTaxDirection === 'income'" name="is" colorClass="text-emerald-500" class="w-3.5 h-3.5" title="Revenu (IS)" />
                    <CategoryPropertyIcon v-if="cat.corporateTaxDirection === 'expense'" name="is" colorClass="text-red-500" class="w-3.5 h-3.5" title="Charge (IS)" />
                  </div>
                  <button
                    v-if="!isReadOnlyMode"
                    class="opacity-30 hover:opacity-100 text-muted-foreground hover:text-foreground transition-opacity ml-auto shrink-0"
                    @click="emit('editCategory', cat)"
                    :title="`Modifier ${cat.name}`"
                    aria-label="Modifier la catégorie"
                  >
                    <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </td>

              <td v-for="month in 12" :key="month" class="min-w-[90px] p-0" :class="month % 2 === 0 ? 'bg-muted/30' : ''">
                <BudgetCell
                  :category-name="cat.name"
                  :month-label="monthNames[month - 1]"
                  type="parameter"
                  :stored-value="cat.months[month] ?? null"
                  :resolved-value="resolvedValues[cat.id]?.[month] ?? null"
                  :has-formula="!!cat.formula"
                  :is-manual-override="cat.monthOverrides[month] ?? false"
                  :is-current-month="month === currentMonth"
                  :is-non-editable="cat.isNonEditable"
                  @save="(amount) => onSaveLine(cat.id, month, amount)"
                  @reset="onResetLine(cat.id, month)"
                />
              </td>

              <td
                class="w-28 min-w-[7rem] px-3 py-1.5 text-right text-xs font-bold tabular-nums"
                :class="cat.isParameterSum ? 'text-indigo-500 dark:text-indigo-400' : 'text-muted-foreground'"
              >
                {{ cat.isParameterSum ? formatParameter(catAnnualTotal(cat.id)) : '-' }}
              </td>
            </tr>
          </template>

        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { BudgetCategoryData, BudgetAccountData } from '../../../composables/useBudget'
import type { ResolvedBudgetData } from '../../../composables/useBudgetFormula'
import { useBudget } from '../../../composables/useBudget'
import { useBudgetFormula } from '../../../composables/useBudgetFormula'
import { useToast } from '../../../composables/useToast.js'
import BudgetCell from './BudgetCell.vue'
import BudgetOpeningRow from './BudgetOpeningRow.vue'
import CategoryPropertyIcon from './CategoryPropertyIcon.vue'

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps<{
  account:       'personal' | 'professional'
  data:          BudgetAccountData
  resolvedValues: ResolvedBudgetData
  year:          number
  previousYearCarry?: number | null
}>()

// ── Emits ─────────────────────────────────────────────────────────────────────

const emit = defineEmits<{
  addCategory:          []
  addCategoryOfType:    [type: 'income' | 'expense']
  editCategory:         [cat: BudgetCategoryData]
  changeAccount:        [account: 'personal' | 'professional']
}>()

// ── Constants ─────────────────────────────────────────────────────────────────

const monthNames   = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
const currentMonth = new Date().getMonth() + 1

const accountLabel = computed(() => props.account === 'professional' ? 'Compte Professionnel' : 'Compte Personnel')
const accountIcon  = computed(() => props.account === 'professional' ? '💼' : '🏠')

// ── Composables ───────────────────────────────────────────────────────────────

const { upsertLine, deleteLine, upsertOpeningBalance, deleteOpeningBalance, isReadOnlyMode } = useBudget()
const { computeMonthlyTotals, computeAnnualTotals, computeOpeningBalances } = useBudgetFormula()
const toast = useToast()

// ── Computed ──────────────────────────────────────────────────────────────────

const incomeCategories  = computed(() => props.data.categories.filter(c => c.type === 'income').sort((a, b) => a.sortOrder - b.sortOrder))
const expenseCategories = computed(() => props.data.categories.filter(c => c.type === 'expense').sort((a, b) => a.sortOrder - b.sortOrder))
const parameterCategories = computed(() => props.data.categories.filter(c => c.type === 'parameter').sort((a, b) => a.sortOrder - b.sortOrder))

const monthlyTotals = computed(() =>
  computeMonthlyTotals(props.data.categories, props.resolvedValues)
)

const annualTotals = computed(() => computeAnnualTotals(monthlyTotals.value))

const resolvedOpeningBalances = computed(() =>
  computeOpeningBalances(props.data.openingBalances, monthlyTotals.value, props.previousYearCarry ?? null)
)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatParameter(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits: 2,
  }).format(amount)
}

function formatBalance(amount: number): string {
  const prefix = amount > 0 ? '+' : ''
  return prefix + formatCurrency(amount)
}

function balanceColor(amount: number): string {
  if (amount > 0)  return 'text-emerald-500 dark:text-emerald-400'
  if (amount < 0)  return 'text-red-500 dark:text-red-400'
  return 'text-muted-foreground'
}

function catAnnualTotal(catId: number): number {
  let total = 0
  for (let m = 1; m <= 12; m++) {
    total += props.resolvedValues[catId]?.[m] ?? 0
  }
  return total
}

function getEndOfMonthBalance(month: number): number | null {
  const opening = resolvedOpeningBalances.value[month]?.amount
  if (opening === null || opening === undefined) return null
  return opening + (monthlyTotals.value[month]?.balance ?? 0)
}

function formatNullableCurrency(amount: number | null): string {
  if (amount === null || amount === undefined) return '—'
  return formatCurrency(amount)
}

function openingBalanceColor(amount: number | null): string {
  if (amount === null || amount === undefined) return 'text-muted-foreground/30'
  if (amount >= 0) return 'text-sky-500 dark:text-sky-400'
  return 'text-orange-500 dark:text-orange-400'
}

// ── Actions ───────────────────────────────────────────────────────────────────

async function onSaveLine(categoryId: number, month: number, amount: number): Promise<void> {
  try {
    await upsertLine(categoryId, props.year, month, amount, true)
  } catch {
    toast.error('Erreur lors de la sauvegarde')
  }
}

async function onResetLine(categoryId: number, month: number): Promise<void> {
  try {
    await deleteLine(categoryId, props.year, month)
  } catch {
    toast.error('Erreur lors de la réinitialisation')
  }
}

async function onSaveOpeningBalance(month: number, amount: number): Promise<void> {
  try {
    await upsertOpeningBalance(props.account, props.year, month, amount, true)
  } catch {
    toast.error('Erreur lors de la sauvegarde du solde')
  }
}

async function onResetOpeningBalance(month: number): Promise<void> {
  try {
    await deleteOpeningBalance(props.account, props.year, month)
  } catch {
    toast.error('Erreur lors de la réinitialisation du solde')
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar { height: 4px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { @apply bg-zinc-700 rounded-full; }

/* ── Sticky column opaque backgrounds ──────────────────────────────────────── */

/* Category data rows: opaque bg-card, opaque hover */
.budget-data-row:hover > td:not(.budget-sticky-cell) {
  background-color: hsl(var(--muted) / 0.2);
}

/* Sticky cell base: opaque background + right shadow separator */
:deep(.budget-sticky-cell),
.budget-sticky-cell {
  background-color: hsl(var(--card)) !important;
  box-shadow: 4px 0 6px -2px hsl(var(--border) / 0.6);
}

/* Sticky cell hover: opaque muted */
.budget-data-row:hover > .budget-sticky-cell {
  background-color: hsl(var(--muted)) !important;
}

/* Section headers (Recettes, Dépenses, Paramètres): shadow separator only, bg set per-section via Tailwind */
.budget-sticky-section {
  box-shadow: 4px 0 6px -2px hsl(var(--border) / 0.6);
}

/* Total rows (Total recettes / Total dépenses) */
.budget-total-row {
  background-color: hsl(var(--muted) / 0.2);
}
.budget-sticky-total {
  background-color: hsl(var(--muted)) !important;
  box-shadow: 4px 0 6px -2px hsl(var(--border) / 0.6);
}
</style>
